import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, ButtonBase, Typography } from "@mui/material";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import { adminTriggerActions } from "../../../backend/store/reducers/adminReducer";
import { useTranslation } from "../../../hooks/useTranslation";

/**
 * "Login as this user" (admin impersonation) indicators — replaces the sticky orange bar
 * that pushed the page down and covered controls, worst on phones (owner, 2026-10-12):
 *  - ImpersonationGlow: an orange glow around the whole screen; takes no space and never
 *    catches the mouse;
 *  - the WORDS stay visible (design rule: status never by colour alone), small and in
 *    the menus: ImpersonationTopBarControl (top bar) and ImpersonationSidePanel (side
 *    menus), each with the way out.
 */
const WARN_TEXT = "#9A5800";
const WARN_BG = "#FFF4E0";
const WARN_LINE = "#F59E0B";

export function useImpersonation() {
  const dispatch = useDispatch();
  const currentUser = useSelector((state: any) => state.authStore.currentUser);
  // Same test App uses: the admin's own token is parked while viewing as someone else
  // (currentUser.is_impersonating is not always filled in by /me).
  let parked = false;
  try { parked = !!localStorage.getItem("admin_original_token"); } catch (e) { /* storage blocked */ }
  const active = parked || !!currentUser?.is_impersonating;
  const name = currentUser ? `${currentUser.first_name ?? ""} ${currentUser.last_name ?? ""}`.trim() : "";
  const account = currentUser?.workspaces?.find((ws: any) => ws.id === currentUser?.currentWorkspace)?.name ?? "";
  const exit = () => dispatch(adminTriggerActions.stopImpersonation());
  return { active, name, account, exit };
}

export const ImpersonationGlow: React.FC = () => {
  const { active, name, account } = useImpersonation();
  const { p } = useTranslation("general");
  if (!active) return null;
  return (
    <>
      <Box
        aria-hidden
        data-testid="impersonation-glow"
        sx={{
          position: "fixed",
          inset: 0,
          pointerEvents: "none",
          // above dialogs and menus so the reminder frames them too; below the import
          // overlay, which must stay on top (importOverlayZIndex.test.ts)
          zIndex: 100000,
          boxShadow: `inset 0 0 0 3px ${WARN_LINE}, inset 0 0 26px 6px rgba(245, 158, 11, 0.45)`,
        }}
      />
      {/* Screen readers hear it once, since the glow itself is decorative. */}
      <Box
        role="status"
        sx={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)", whiteSpace: "nowrap" }}
      >
        {p("impersonation_announcement", { name, account })}
      </Box>
    </>
  );
};

/** Top bar: "Viewing as <name> · Exit" on desktop, a short "Exit" on phones. */
export const ImpersonationTopBarControl: React.FC = () => {
  const { active, name, exit } = useImpersonation();
  const { p } = useTranslation("general");
  if (!active) return null;
  const label = p("impersonation_exit_aria", { name });
  const pill = {
    minHeight: 40,
    px: 1.5,
    mx: 1,
    borderRadius: "999px",
    border: `1.5px solid ${WARN_LINE}`,
    backgroundColor: WARN_BG,
    color: WARN_TEXT,
    fontWeight: 700,
    fontSize: 14,
    gap: 0.75,
    // 48px tap area
    "&::after": { content: '""', position: "absolute", inset: "-6px -2px" },
    "&:focus-visible": { outline: "3px solid #0163D1", outlineOffset: 2 },
  } as const;
  return (
    <>
      <ButtonBase onClick={exit} aria-label={label} sx={{ ...pill, display: { xs: "none", md: "inline-flex" }, maxWidth: 360 }}>
        <VisibilityRoundedIcon aria-hidden sx={{ fontSize: 18 }} />
        <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {p("impersonation_label")} <bdi>{name}</bdi>
        </Box>
        <Box component="span" aria-hidden sx={{ opacity: 0.6 }}>·</Box>
        <Box component="span" sx={{ textDecoration: "underline", whiteSpace: "nowrap" }}>{p("impersonation_exit")}</Box>
      </ButtonBase>
      {/* Phone: the bar is already full, so just the word; the side menu says who. */}
      <ButtonBase
        onClick={exit}
        aria-label={label}
        sx={{ ...pill, display: { xs: "inline-flex", md: "none" }, px: 1, mx: 0, minHeight: 36, fontSize: 13, flexShrink: 0, "&::after": { content: '""', position: "absolute", inset: "-6px 0" } }}
      >
        {p("impersonation_exit")}
      </ButtonBase>
    </>
  );
};

/** Side menus: who you're viewing as, in which account, and the way back. */
export const ImpersonationSidePanel: React.FC = () => {
  const { active, name, account, exit } = useImpersonation();
  const { p } = useTranslation("general");
  if (!active) return null;
  return (
    <Box
      sx={{
        mx: 1.5,
        mt: 1.5,
        // clears the active menu tab's rounded corner just below
        mb: 3.5,
        p: 1.5,
        position: "relative",
        zIndex: 3, // above the active tab's curve (CustomSideTab.scss, z-index 2)
        borderRadius: "14px",
        border: `1.5px solid ${WARN_LINE}`,
        backgroundColor: WARN_BG,
        color: WARN_TEXT,
      }}
    >
      <Typography sx={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", display: "flex", alignItems: "center", gap: 0.5 }}>
        <VisibilityRoundedIcon aria-hidden sx={{ fontSize: 16 }} />
        {p("impersonation_label")}
      </Typography>
      <Typography sx={{ fontSize: 15, fontWeight: 700, color: "#0E2A5C", mt: 0.5 }}>
        <bdi>{name}</bdi>
      </Typography>
      {account && account !== name && (
        <Typography sx={{ fontSize: 13, color: "#3F4A6B" }}>
          <bdi>{account}</bdi>
        </Typography>
      )}
      <ButtonBase
        onClick={exit}
        sx={{
          mt: 1.25,
          width: "100%",
          minHeight: 44,
          borderRadius: "14px",
          backgroundColor: WARN_TEXT,
          color: "#fff",
          fontWeight: 700,
          fontSize: 14,
          gap: 0.75,
          "&:focus-visible": { outline: "3px solid #0163D1", outlineOffset: 2 },
        }}
      >
        <LogoutRoundedIcon aria-hidden sx={{ fontSize: 18 }} />
        {p("impersonation_return")}
      </ButtonBase>
    </Box>
  );
};
